import * as React from 'react'
import { Container } from './Container'
import geometricM from './images/geometric-m.png'
import styles from './styles.module.scss'
import Image from 'next/image'
import clsx from 'clsx'
import { DiamondIcon } from '@partials/tech-summit/DiamondIcon'
import Link from 'next/link'
import { Dialog, Transition } from '@headlessui/react'
import { useRouter } from 'next/router'
import { SpeakerDetail } from './SpeakerDetail'
import { ISpeaker } from 'pages/summit'

function Angle() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="86"
      height="86"
      className="absolute -top-1 -right-1"
      viewBox="0 0 86 86"
      fill="none">
      <path
        d="M80.5 80.9102L80.5 10.9102C80.5 8.14873 78.2614 5.91016 75.5 5.91016L5.5 5.91016"
        stroke="url(#paint0_linear_88_4148)"
        stroke-width="10"
        stroke-linecap="round"
      />
      <defs>
        <linearGradient
          id="paint0_linear_88_4148"
          x1="80.5"
          y1="80.9102"
          x2="49.5541"
          y2="-9.2163"
          gradientUnits="userSpaceOnUse">
          <stop stop-color="#F3EBFF" />
          <stop offset="0.5" stop-color="#B98DFF" />
          <stop offset="1" stop-color="#5D2EFF" />
        </linearGradient>
      </defs>
    </svg>
  )
}

const Speaker = ({ speaker }: { speaker: ISpeaker }) => {
  const router = useRouter()

  const closeModal = () => {
    router.push('/summit#speakers')
  }

  return (
    <>
      <div>
        <Link
          href={`/summit?speaker=${speaker.slug}`}
          as={`/summit/speakers/${speaker.slug}`}>
          <div className="relative w-full h-auto aspect-square mb-5 md:mb-7 bg-secondary-blue rounded-5xl rounded-tr-none">
            {speaker.image && (
              <Image
                className="object-cover object-top rounded-5xl rounded-tr-none overflow-clip grayscale"
                src={speaker.image.url}
                alt={speaker.name}
                blurDataURL={speaker.image?.blurUpThumb}
                fill
              />
            )}
            <Angle />
          </div>
        </Link>
        <h3 className="font-bold text-2xl">{speaker.name}</h3>
        <p className="mt-1">{speaker.role}</p>
      </div>
      <Transition
        appear
        show={router.query.speaker === speaker.slug}
        as={React.Fragment}>
        <Dialog as="div" className="relative z-[1000]" onClose={closeModal}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden bg-[#EEF2FF] align-middle shadow-xl transition-all p-4 xl:p-6 rounded-lg">
                  <SpeakerDetail speaker={speaker} />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export function Speakers({ speakers }: { speakers: ISpeaker[] }) {
  return (
    <div className="bg-[#290A55] relative">
      <a id="speakers" />
      <Image
        src={geometricM}
        alt=""
        unoptimized
        className="absolute right-0 w-44 rotate-180 top-0"
      />
      <Image
        src={geometricM}
        alt=""
        unoptimized
        className="absolute left-0 w-36 bottom-40"
      />
      <Container className="relative py-14 lg:py-28">
        <h2
          className={clsx(
            styles.sectionTitleGradient,
            'font-body md:text-6xl lg:text-7xl sm:text-5xl text-4xl font-bold leading-relaxed md:leading-relaxed lg:leading-relaxed md:text-center mb-5',
          )}>
          Key Speakers
        </h2>
        <p className="text-white text-2xl md:text-center max-w-4xl md:mx-auto mb-20">
          Hear from industry leaders, innovators, and visionaries who are
          shaping the future of technology and innovation. Our speakers are
          pioneers in their fields, leading the way in technological advancement
          and innovative thinking.
        </p>
        <div className="flex gap-4 mb-12">
          <div className="flex flex-col items-center pt-1 pb-1.5">
            <DiamondIcon />
            <span className="w-px bg-white/75 flex-grow" />
          </div>
          <div className="text-white">
            <p className="text-sm">Speakers</p>
            <time
              dateTime="September 26th, 2023"
              className="text-2xl font-bold">
              September 26th
            </time>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 sm:gap-14 lg:grid-cols-4 lg:gap-y-28 text-white">
          {speakers.map((speaker, index) => (
            <Speaker key={index} speaker={speaker} />
          ))}
        </div>
      </Container>
    </div>
  )
}
